// Brazilian document helpers: digits, check digits and input masks. Pure, framework-free.

/** Strips everything but digits. Tolerates null-ish input coming from optional fields. */
export function onlyDigits(v: string): string {
  return (v || '').replace(/\D/g, '');
}

/** Whether a CPF (masked or not) has valid check digits. Rejects repeated-digit sequences. */
export function isValidCPF(raw: string): boolean {
  const cpf = onlyDigits(raw);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf[i], 10) * (10 - i);
  let rev = 11 - (sum % 11);
  if (rev >= 10) rev = 0;
  if (rev !== parseInt(cpf[9], 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf[i], 10) * (11 - i);
  rev = 11 - (sum % 11);
  if (rev >= 10) rev = 0;
  return rev === parseInt(cpf[10], 10);
}

/** Whether a numeric CNPJ (masked or not) has valid check digits. Rejects repeated-digit sequences. */
export function isValidCNPJ(raw: string): boolean {
  const cnpj = onlyDigits(raw);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
  let len = cnpj.length - 2;
  let nums = cnpj.substring(0, len);
  const dv = cnpj.substring(len);
  let sum = 0;
  let pos = len - 7;
  for (let i = len; i >= 1; i--) {
    sum += Number(nums.charAt(len - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  let res = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (res !== parseInt(dv.charAt(0), 10)) return false;
  len += 1;
  nums = cnpj.substring(0, len);
  sum = 0;
  pos = len - 7;
  for (let i = len; i >= 1; i--) {
    sum += Number(nums.charAt(len - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  res = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return res === parseInt(dv.charAt(1), 10);
}

/** A pragmatic e-mail shape check; the server remains the authority. */
export function isEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
}

/** Formats CPF digits as `000.000.000-00` while typing. */
export function maskCPF(v: string): string {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return d;
}

/** Formats CNPJ digits as `00.000.000/0000-00` while typing. */
export function maskCNPJ(v: string): string {
  const d = onlyDigits(v).slice(0, 14);
  if (d.length > 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  if (d.length > 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  if (d.length > 5) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`;
  return d;
}

/** Formats CEP digits as `00000-000` while typing. */
export function maskCEP(v: string): string {
  const d = onlyDigits(v).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** Keeps at most `max` digits. */
export function maskDigits(v: string, max: number): string {
  return onlyDigits(v).slice(0, max);
}
